/**
 * SellerCurePanel — the seller's decision after the buyer's "not satisfied"
 * notice (design T9/T11; copy §3 `WI.seller_cure.*`).
 *
 * `cure_pending`: the cure window and its deadline. `cure_terms_unclear`
 * (Q-L1 interim): the offer doesn't say whether a cure is allowed, so the
 * panel says so; the seller may still deliver either notice.
 *
 * The actions shown are exactly the server's `allowed_actions`.
 */

import React from 'react';
import { NOTICE_TYPES } from '../../../agents/contingencyNoticesAgent';
import { contractCopy, hasPlaceholder } from './contractCopy';
import {
  CONTRACT_STATUS,
  buyerNoticeDate,
  cureDeadlineDate,
  declineNoticeDate,
  sellerDeclineNotice,
} from './contractFormat';
import styles from './SellerCurePanel.module.css';

/** Whether the panel applies to this contract block for this role. */
export function showsSellerCurePanel(contract, role) {
  if (role !== 'seller' || !contract) return false;
  return [CONTRACT_STATUS.CURE_PENDING, CONTRACT_STATUS.CURE_TERMS_UNCLEAR].includes(contract.status);
}

export default function SellerCurePanel({ contingencyId, contract, onDeliver, noticesAvailable = true }) {
  const allowed = contract.allowed_actions || [];
  const vars = {
    buyer_notice_date: buyerNoticeDate(contract),
    heading: contract.heading,
    cure_deadline_date: cureDeadlineDate(contract),
    decline_notice_date: declineNoticeDate(contract),
  };
  const unclear = contract.status === CONTRACT_STATUS.CURE_TERMS_UNCLEAR;
  // After the seller declines, the panel says so instead of asking again.
  const declined = !unclear && Boolean(sellerDeclineNotice(contract));
  const panelKey = unclear
    ? 'WI.seller_cure.unclear_panel'
    : declined ? 'WI.seller_cure.panel.declined' : 'WI.seller_cure.panel';
  let text = contractCopy(panelKey, vars);
  if (hasPlaceholder(text)) text = null;
  const declineEffect = contractCopy('WI.seller_cure.decline_effect', vars);
  const canCure = noticesAvailable && allowed.includes(NOTICE_TYPES.SELLER_CURE);
  const canDecline = noticesAvailable && allowed.includes(NOTICE_TYPES.SELLER_DECLINES_CURE);
  const headingId = `seller-cure-${contingencyId}`;

  return (
    <section className={styles.panel} aria-labelledby={headingId} data-testid={`seller-cure-panel-${contingencyId}`}>
      <h4 id={headingId} className={styles.heading}>Your decision</h4>
      {text && <p className={styles.text}>{text}</p>}
      {(canCure || canDecline) && (
        <div className={styles.actions}>
          {canCure && (
            <button
              type="button"
              className={styles.cure}
              onClick={() => onDeliver(NOTICE_TYPES.SELLER_CURE)}
              data-testid={`seller-cure-${contingencyId}`}
            >
              {contractCopy('WI.seller_cure.action.cure')}
            </button>
          )}
          {canDecline && (
            <button
              type="button"
              className={styles.decline}
              onClick={() => onDeliver(NOTICE_TYPES.SELLER_DECLINES_CURE)}
              aria-describedby={!unclear && !hasPlaceholder(declineEffect) ? `${headingId}-decline` : undefined}
              data-testid={`seller-decline-${contingencyId}`}
            >
              {contractCopy('WI.seller_cure.action.decline')}
            </button>
          )}
        </div>
      )}
      {canDecline && !unclear && !hasPlaceholder(declineEffect) && (
        <p id={`${headingId}-decline`} className={styles.help}>{declineEffect}</p>
      )}
    </section>
  );
}
